import React, { useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Check,
  CheckCheck,
  ChevronRight,
  Clock3,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  Paperclip,
  Plane,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import llotjaPhoto from "../../assets/venues/llotja-de-mar.jpg";
import batlloPhoto from "../../assets/venues/casa-batllo.jpg";
import santPauPhoto from "../../assets/venues/sant-pau.jpg";

interface WorkspaceSceneProps {
  section: string;
  onNavigate: (section: string) => void;
}

const COSTS = [
  { label: "Venue", note: "4 days · exclusive hire", a: 18500, b: 12900 },
  { label: "Hotels", note: "140 rooms · 3 nights", a: 46200, b: 37800 },
  { label: "Flights", note: "132 travellers · 5 origins", a: 13742, b: 12100 },
  {
    label: "Gala dinner",
    note: "140 guests · service included",
    a: 9800,
    b: 9800,
  },
  {
    label: "AV & production",
    note: "Main stage + breakout rooms",
    a: 6400,
    b: 6400,
  },
];
const money = (value: number) => `£${value.toLocaleString("en-GB")}`;
const THREADS = [
  {
    name: "Casa Llimona",
    initials: "CL",
    subject: "Gala dinner · revised quote",
    time: "10:42",
    status: "Quote received",
    body: "Thank you, Hannah. We can include service at £70 per guest for the three-course menu. Vegetarian and vegan options are included.",
    total: "£9,800",
    note: "140 guests · £70 per person · service included",
    colour: "sage",
  },
  {
    name: "Forn Verd",
    initials: "FV",
    subject: "Dinner for 140 · menu options",
    time: "09:18",
    status: "Quote received",
    body: "Our three-course menu is £76 per guest. Service is extra; we can price that once you confirm the timings.",
    total: "£10,640",
    note: "140 guests · £76 per person · service extra",
    colour: "rose",
  },
  {
    name: "Taula Oberta",
    initials: "TO",
    subject: "Following up on availability",
    time: "Yesterday",
    status: "Awaiting reply",
    body: "Are you available for dinner for 140 guests on 11 March? Please send a quote with service and tax listed separately.",
    total: "Pending",
    note: "Follow-up sent · awaiting supplier response",
    colour: "warm",
  },
];

const WorkspaceScene: React.FC<WorkspaceSceneProps> = ({
  section,
  onNavigate,
}) => {
  const [thread, setThread] = useState(0);
  const [version, setVersion] = useState<"a" | "b">("a");
  const [approved, setApproved] = useState(false);
  const [guestFilter, setGuestFilter] = useState("");
  const [scanned, setScanned] = useState(false);
  const selected = THREADS[thread];

  if (section === "quotes")
    return (
      <div className="ew-scene ew-inbox">
        <div className="ew-inbox-list">
          <div className="ew-section-title">
            <h3>Supplier conversations</h3>
            <span>3</span>
          </div>
          <div className="ew-inbox-filter">
            <span>All conversations</span>
            <span>
              Recent <ArrowDown size={10} aria-hidden="true" />
            </span>
          </div>
          {THREADS.map((item, index) => (
            <button
              type="button"
              key={item.name}
              className={`ew-thread ${thread === index ? "is-selected" : ""}`}
              aria-pressed={thread === index}
              onClick={() => setThread(index)}
            >
              <span className={`ew-avatar ew-avatar--${item.colour}`}>
                {item.initials}
              </span>
              <span>
                <b>{item.name}</b>
                <span>{item.subject}</span>
                <small>
                  <i className={index === 2 ? "is-amber" : ""} />
                  {item.status}
                </small>
              </span>
              <time>{item.time}</time>
            </button>
          ))}
          <div className="ew-inbox-foot">
            <Sparkles size={13} aria-hidden="true" /> Follow-ups handled by
            Comtev
          </div>
        </div>
        <div className="ew-conversation">
          <div className="ew-mail-heading">
            <h3>{selected.subject}</h3>
            <span>
              <Mail size={12} aria-hidden="true" /> Email · {selected.name}
            </span>
          </div>
          <div className="ew-mail">
            <div className="ew-mail-byline">
              <span className="ew-avatar ew-avatar--warm">C</span>
              <span>
                <b>Comtev</b> <small>on behalf of Hannah</small>
                <span>To: {selected.name} · Mon, 09:12</span>
              </span>
              <CheckCheck size={15} aria-hidden="true" />
            </div>
            <p>Hello {selected.name},</p>
            <p>
              We&apos;re planning a company kickoff in Barcelona. Can you quote
              for a three-course dinner for 140 people on 11 March? Please list
              service and tax separately.
            </p>
            <span className="ew-attachment">
              <Paperclip size={12} aria-hidden="true" /> Barcelona kickoff ·
              brief.pdf
            </span>
          </div>
          <div className="ew-mail ew-mail--reply">
            <div className="ew-mail-byline">
              <span className={`ew-avatar ew-avatar--${selected.colour}`}>
                {selected.initials}
              </span>
              <span>
                <b>{thread === 2 ? "Comtev · follow-up" : selected.name}</b>
                <span>
                  Tue, {selected.time === "Yesterday" ? "09:00" : selected.time}
                </span>
              </span>
            </div>
            <p>{selected.body}</p>
          </div>
          <div className="ew-quote-result">
            <span>
              <Check size={13} aria-hidden="true" />
              {thread === 2 ? "Follow-up sent" : "Quote received"}
            </span>
            <div>
              <strong>{selected.total}</strong>
              <small>{selected.note}</small>
            </div>
          </div>
        </div>
      </div>
    );

  if (section === "budget") {
    const total = COSTS.reduce((sum, item) => sum + item[version], 0);
    return (
      <div className="ew-scene ew-budget">
        <div className="ew-section-title">
          <h3>Event budget</h3>
          <div className="ew-segment" aria-label="Budget version">
            {(["a", "b"] as const).map((v) => (
              <button
                type="button"
                key={v}
                aria-pressed={version === v}
                onClick={() => setVersion(v)}
              >
                {v === "a" ? "A · Waterfront" : "B · Lean"}
              </button>
            ))}
          </div>
        </div>
        <div className="ew-budget-summary">
          <div>
            <span>Forecast total</span>
            <strong>{money(total)}</strong>
            <small>{money(Math.round(total / 140))} per guest</small>
          </div>
          <div>
            <span>Budget ceiling</span>
            <b>£100,000</b>
            <small>Set by Hannah Reid</small>
          </div>
          <div>
            <span>Budget remaining</span>
            <b className="ew-green">{money(100000 - total)}</b>
            <small>Within your budget</small>
          </div>
        </div>
        <div className="ew-table-scroll">
          <table className="ew-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Waterfront</th>
                <th>Lean</th>
                <th>Difference</th>
              </tr>
            </thead>
            <tbody>
              {COSTS.map((line) => (
                <tr key={line.label}>
                  <td>
                    <b>{line.label}</b>
                    <small>{line.note}</small>
                  </td>
                  <td className={version === "a" ? "ew-selected-col" : ""}>
                    {money(line.a)}
                  </td>
                  <td className={version === "b" ? "ew-selected-col" : ""}>
                    {money(line.b)}
                  </td>
                  <td className={line.a !== line.b ? "ew-green" : ""}>
                    {line.a !== line.b ? `−${money(line.a - line.b)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ew-bottom-note">
          <span>
            <FileText size={14} aria-hidden="true" /> Estimates in GBP · Subject
            to availability and final supplier terms
          </span>
          <button type="button" onClick={() => onNavigate("signoff")}>
            Review approval <ArrowRight size={13} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  if (section === "brief")
    return (
      <div className="ew-scene ew-overview">
        <div className="ew-overview-main">
          <div className="ew-section-title">
            <h3>Event brief</h3>
            <span>Updated Monday, 09:16</span>
          </div>
          <div className="ew-brief-message">
            <div className="ew-mail-byline">
              <span className="ew-avatar ew-avatar--warm">HR</span>
              <span>
                <b>Hannah Reid</b>
                <span>Monday, 09:02</span>
              </span>
            </div>
            <p>
              Barcelona kickoff, 9–12 March, 140 people. We need a venue, hotel
              rooms and flights. Dinner on 11 March too. Budget is £100,000.
            </p>
            <div className="ew-brief-tags">
              <span>
                <MapPin size={11} aria-hidden="true" /> Barcelona
              </span>
              <span>
                <Users size={11} aria-hidden="true" /> 140 guests
              </span>
              <span>£100,000 budget</span>
            </div>
          </div>
          <div className="ew-agent-response">
            <span className="ew-agent-mark">
              <Sparkles size={15} aria-hidden="true" />
            </span>
            <div>
              <b>Venue, dinner and travel searches started.</b>
              <p>
                Three venues are shortlisted. Dinner and AV suppliers have the
                brief, and flight estimates are ready for all five departure
                cities. Review the quotes, flights and hotel rooms when you are
                ready.
              </p>
              <div className="ew-task-lines">
                {[
                  ["Venue shortlist", "3 Barcelona venues", "venues"],
                  ["Supplier outreach", "Dinner and AV contacted", "quotes"],
                  ["Flights", "5 departure cities", "flights"],
                  ["Hotels", "140 rooms · 3 nights", "hotels"],
                ].map(([title, note, id]) => (
                  <button type="button" key={id} onClick={() => onNavigate(id)}>
                    <Check size={12} aria-hidden="true" />
                    <span>{title}</span>
                    <small>{note}</small>
                    <ChevronRight size={12} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="ew-composer">
            <span>Sample conversation</span>
            <span>
              <Paperclip size={14} aria-hidden="true" />
              <ArrowUpRight size={15} aria-hidden="true" />
            </span>
          </div>
        </div>
        <aside className="ew-activity">
          <p className="ew-mini-label">BUDGET ESTIMATE</p>
          <div className="ew-forecast">
            <span>Working forecast</span>
            <strong>£94,642</strong>
            <small>of £100,000 budget</small>
            <div className="ew-cost-strip" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </div>
            <span className="ew-green">£5,358 headroom</span>
          </div>
          <p className="ew-mini-label">RECENT ACTIVITY</p>
          <ol className="ew-activity-list">
            <li>
              <span className="ew-activity-dot" />
              <div>
                <b>Venue shortlist ready</b>
                <p>3 venues shortlisted</p>
                <small>09:08 · Venues</small>
              </div>
            </li>
            <li>
              <span className="ew-activity-dot" />
              <div>
                <b>Supplier outreach started</b>
                <p>Dinner and AV requests sent</p>
                <small>09:12 · Vendors</small>
              </div>
            </li>
            <li>
              <span className="ew-activity-dot" />
              <div>
                <b>Travel estimate prepared</b>
                <p>132 travellers, 5 departure cities</p>
                <small>09:16 · Flights</small>
              </div>
            </li>
          </ol>
          <button
            type="button"
            className="ew-text-button"
            onClick={() => onNavigate("quotes")}
          >
            See the conversations <ArrowRight size={12} aria-hidden="true" />
          </button>
        </aside>
      </div>
    );

  if (section === "venues")
    return (
      <div className="ew-scene ew-venues">
        <div className="ew-section-title">
          <h3>Venue shortlist</h3>
          <span>Barcelona · 3 spaces to explore</span>
        </div>
        <div className="ew-venue-grid">
          {[
            {
              name: "Casa Llotja de Mar",
              area: "El Born · Saló de Contractacions",
              price: "£18,500",
              capacity: "430 at dinner",
              detail:
                "A medieval trading hall with stone arches, 14-metre ceilings and a chequered marble floor.",
              caution: "Room for the full 140-person dinner",
              photo: llotjaPhoto,
              alt: "Stone columns and Gothic arches above the chequered floor at Casa Llotja de Mar",
              url: "https://www.llotjademar.cat/en/contracting-hall",
              credit: "Press Cambrabcn",
              source:
                "https://commons.wikimedia.org/wiki/File:Sal%C3%B3_de_contractacions_-_45587886644.jpg",
              license: "2.0",
            },
            {
              name: "Recinte Modernista Sant Pau",
              area: "El Guinardó · Domènech i Montaner room",
              price: "£12,900",
              capacity: "200 at reception",
              detail:
                "Mosaic walls, stained glass and views across the Modernista complex from the first floor.",
              caution: "Dinner layout limited to 120 guests",
              photo: santPauPhoto,
              alt: "Decorated ceiling and mosaic walls in Sant Pau's Domènech i Montaner room",
              url: "https://santpaubarcelona.org/en/lloguer-despais/",
              credit: "Jordiferrer",
              source:
                "https://commons.wikimedia.org/wiki/File:Sala_Dom%C3%A8nech_i_Montaner_(Pavell%C3%B3_de_l%27Administraci%C3%B3)_01.JPG",
              license: "4.0",
            },
            {
              name: "Casa Batlló",
              area: "Passeig de Gràcia · Noble Floor",
              price: "£21,000",
              capacity: "Layout on request",
              detail:
                "Gaudí's curved ceilings and coloured glass, with a courtyard for private receptions.",
              caution: "Confirm capacity for the chosen layout",
              photo: batlloPhoto,
              alt: "Curved windows and blue stained glass in Casa Batlló's Noble Floor salon",
              url: "https://www.casabatllo.es/en/events/",
              credit: "Sara Terrones",
              source:
                "https://commons.wikimedia.org/wiki/File:CasaBatll%C3%B3_NobleFloor_saloon_stainedglass.jpg",
              license: "4.0",
            },
          ].map((venue) => (
            <article className="ew-venue" key={venue.name}>
              <a
                className="ew-venue-photo"
                href={venue.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Explore ${venue.name} (opens in a new tab)`}
              >
                <img
                  src={venue.photo}
                  alt={venue.alt}
                  width="960"
                  height="720"
                  loading="lazy"
                  decoding="async"
                />
                <span>
                  View venue <ArrowUpRight size={12} aria-hidden="true" />
                </span>
              </a>
              <div className="ew-venue-info">
                <span className="ew-venue-area">
                  <MapPin size={11} aria-hidden="true" />
                  {venue.area}
                </span>
                <h4>{venue.name}</h4>
                <p>{venue.detail}</p>
                <div className="ew-venue-facts">
                  <div>
                    <small>Sample allowance</small>
                    <b>{venue.price}</b>
                  </div>
                  <span>{venue.capacity}</span>
                </div>
                <small>
                  <Clock3 size={11} aria-hidden="true" />
                  {venue.caution}
                </small>
                <details className="ew-photo-credit">
                  <summary>Photo credit</summary>
                  <a
                    href={venue.source}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {venue.credit}
                  </a>
                  {" · "}
                  <a
                    href={`https://creativecommons.org/licenses/by-sa/${venue.license}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    CC BY-SA {venue.license}
                  </a>
                  <span> · Resized and cropped</span>
                </details>
              </div>
            </article>
          ))}
        </div>
        <div className="ew-bottom-note">
          <span>Real venues · Sample allowances, not venue quotes</span>
          <button type="button" onClick={() => onNavigate("budget")}>
            Compare in budget <ArrowRight size={13} aria-hidden="true" />
          </button>
        </div>
      </div>
    );

  if (section === "flights")
    return (
      <div className="ew-scene ew-travel">
        <div className="ew-section-title">
          <h3>Flights to Barcelona</h3>
          <span>
            <Plane size={13} aria-hidden="true" /> Flight estimates
          </span>
        </div>
        <div className="ew-travel-layout">
          <div
            className="ew-route-map"
            aria-label="London, Manchester, Dublin, Berlin and Amsterdam connect to Barcelona"
          >
            <svg
              viewBox="0 0 480 280"
              role="img"
              aria-label="Five flight paths converge on Barcelona"
            >
              <defs>
                <linearGradient id="route-line" x1="0" y1="0" x2="1" y2="1">
                  <stop stopColor="#97a4ab" />
                  <stop offset="1" stopColor="#bd6845" />
                </linearGradient>
              </defs>
              <path
                className="ew-continent"
                d="M166 8l20 33-12 20 10 32-18 21 25 19-8 27 34 5 14-23 47 3 24-37 56 7 25 46-10 24-44-3-17 29-48 7-33-23-28 34-58-9-21-44-22-11 14-24-34-32 10-38-18-18 18-22 26 5z"
              />
              {[
                [155, 100],
                [144, 64],
                [103, 79],
                [310, 95],
                [235, 73],
              ].map(([x, y], i) => (
                <g key={x}>
                  <path
                    d={`M${x} ${y} Q${x + 50} 180 235 229`}
                    fill="none"
                    stroke="url(#route-line)"
                    strokeWidth="1"
                  />
                  <circle cx={x} cy={y} r="3" fill="#728086" />
                  <text x={x - 12} y={y - 11}>
                    {["LHR", "MAN", "DUB", "BER", "AMS"][i]}
                  </text>
                </g>
              ))}
              <circle cx="235" cy="229" r="15" fill="#cb7845" opacity=".12" />
              <circle cx="235" cy="229" r="5" fill="#b96237" />
              <text x="250" y="234" className="ew-map-destination">
                Barcelona
              </text>
            </svg>
            <span>132 travellers · 8 already in Barcelona</span>
          </div>
          <div className="ew-flight-list">
            {[
              ["LHR", "London", "58"],
              ["MAN", "Manchester", "24"],
              ["DUB", "Dublin", "19"],
              ["BER", "Berlin", "17"],
              ["AMS", "Amsterdam", "14"],
            ].map(([code, city, people]) => (
              <div key={code}>
                <span className="ew-airport">{code}</span>
                <span>
                  {city}
                  <small>{people} travellers</small>
                </span>
                <ArrowRight size={12} aria-hidden="true" />
                <b>BCN</b>
                <Check size={13} className="ew-green" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
        <div className="ew-travel-total">
          <span>
            <b>£13,742</b> <small>combined flight estimate</small>
          </span>
          <span>
            <ShieldCheck size={14} aria-hidden="true" /> Estimate only. Nothing
            ticketed.
          </span>
        </div>
      </div>
    );

  if (section === "hotels")
    return (
      <div className="ew-scene ew-travel ew-hotels">
        <div className="ew-section-title">
          <h3>Hotels in Barcelona</h3>
          <span>9–12 March 2027 · 140 rooms</span>
        </div>
        <div className="ew-hotel-layout">
          <article className="ew-hotel-room">
            <p className="ew-mini-label">ROOM OPTION</p>
            <h4>The Hoxton, Poblenou</h4>
            <p>Barcelona · Roomy</p>
            <a
              className="ew-text-button"
              href="https://thehoxton.com/poblenou/rooms/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View the hotel <ArrowUpRight size={12} aria-hidden="true" />
            </a>
            <dl className="ew-hotel-details">
              <div><dt>Check-in</dt><dd>9 March 2027</dd></div>
              <div><dt>Check-out</dt><dd>12 March 2027</dd></div>
              <div><dt>Rooms</dt><dd>140 · single occupancy</dd></div>
              <div><dt>Sample rate</dt><dd>£110 per room / night</dd></div>
              <div><dt>Sample cancellation terms</dt><dd>Refundable until 2 March</dd></div>
            </dl>
          </article>
          <aside className="ew-hotel-booking">
            <p className="ew-mini-label">BOOK IN COMTEV</p>
            <h4>Three nights for the team.</h4>
            <dl className="ew-hotel-details">
              <div><dt>140 rooms × £110 × 3 nights</dt><dd>£46,200</dd></div>
              <div><dt>Sample total</dt><dd>£46,200</dd></div>
            </dl>
            <p>Review the room and cancellation terms before booking. Company bookings need spend approval.</p>
            <button
              type="button"
              className="ew-primary"
              onClick={() => onNavigate("signoff")}
            >
              Review booking <ArrowRight size={14} aria-hidden="true" />
            </button>
            <small>Opens the sample approval request. No reservation is made.</small>
          </aside>
        </div>
        <div className="ew-bottom-note">
          <span>Real hotel · Illustrative rate, terms and availability</span>
          <button type="button" onClick={() => onNavigate("budget")}>
            See in budget <ArrowRight size={13} aria-hidden="true" />
          </button>
        </div>
      </div>
    );

  if (section === "signoff")
    return (
      <div className="ew-scene ew-approval">
        <div className="ew-approval-document">
          <div className="ew-section-title">
            <span className="ew-mini-label">REQUEST #BCN-014</span>
            <span className={approved ? "ew-green" : "ew-amber"}>
              {approved ? "Approved in sample" : "Needs your approval"}
            </span>
          </div>
          <h3>
            Barcelona kickoff
            <br />
            Travel & accommodation
          </h3>
          <p>Prepared by Hannah Reid · For James Morgan, Finance</p>
          <dl>
            <div>
              <dt>Hotel estimate</dt>
              <dd>£46,200</dd>
            </div>
            <div>
              <dt>Flight estimate</dt>
              <dd>£13,742</dd>
            </div>
            <div>
              <dt>Requested ceiling</dt>
              <dd>£59,942</dd>
            </div>
          </dl>
          <div className="ew-approval-note">
            <ShieldCheck size={17} aria-hidden="true" />
            <p>
              This request covers flights and hotels only. Venue and supplier
              payments are arranged separately.
            </p>
          </div>
          <button
            className="ew-primary"
            type="button"
            disabled={approved}
            onClick={() => setApproved(true)}
          >
            {approved ? (
              <>
                <Check size={15} aria-hidden="true" /> Sample approval recorded
              </>
            ) : (
              <>
                Try approving this request{" "}
                <ArrowRight size={14} aria-hidden="true" />
              </>
            )}
          </button>
          <small>No real booking or payment will be made.</small>
        </div>
        <aside className="ew-decision-trail">
          <p className="ew-mini-label">APPROVAL HISTORY</p>
          <ol className="ew-activity-list">
            <li>
              <span className="ew-activity-dot" />
              <div>
                <b>Hannah prepared the request</b>
                <p>Costs linked from the event budget</p>
                <small>Monday · 10:20</small>
              </div>
            </li>
            <li>
              <span className="ew-activity-dot" />
              <div>
                <b>Sent to James for review</b>
                <p>Named approver · Finance</p>
                <small>Monday · 10:21</small>
              </div>
            </li>
            <li>
              <span
                className={`ew-activity-dot ${approved ? "" : "is-pending"}`}
              />
              <div aria-live="polite">
                <b>
                  {approved
                    ? "James approved the sample"
                    : "Awaiting James's approval"}
                </b>
                <p>
                  {approved
                    ? "Approved ceiling: £59,942"
                    : "Nothing charged before approval"}
                </p>
                <small>
                  {approved
                    ? "Just now · Sample action"
                    : "Approval expires after 24 hours"}
                </small>
              </div>
            </li>
          </ol>
        </aside>
      </div>
    );

  if (section === "guests") {
    const guests = [
      ["Amelia Shaw", "Product", "London", "Confirmed"],
      ["Oliver Chen", "Engineering", "Berlin", "Confirmed"],
      ["Sofia Martins", "Design", "Amsterdam", "Confirmed"],
      ["Noah Williams", "Sales", "Manchester", "Awaiting RSVP"],
      ["Isla Murphy", "People", "Dublin", "Confirmed"],
    ].filter((guest) =>
      guest.join(" ").toLowerCase().includes(guestFilter.toLowerCase()),
    );
    return (
      <div className="ew-scene ew-guests">
        <div className="ew-section-title">
          <h3>Guest list</h3>
          <span>131 of 140 names collected</span>
        </div>
        <div className="ew-guest-toolbar">
          <div>
            <span className="ew-avatar ew-avatar--sage">
              <MessageSquare size={14} aria-hidden="true" />
            </span>
            <span>
              <b>Waiting for 9 guest names</b>
              <small>
                Comtev follows up with team leads on Slack and WhatsApp
              </small>
            </span>
          </div>
          <input
            aria-label="Search sample guests"
            placeholder="Search guests…"
            value={guestFilter}
            onChange={(event) => setGuestFilter(event.target.value)}
          />
        </div>
        <div className="ew-table-scroll">
          <table className="ew-table">
            <thead>
              <tr>
                <th>Guest</th>
                <th>Team</th>
                <th>Travelling from</th>
                <th>RSVP</th>
              </tr>
            </thead>
            <tbody>
              {guests.map(([name, team, city, status]) => (
                <tr key={name}>
                  <td>
                    <span className="ew-guest-name">
                      <span className="ew-avatar">
                        {name
                          .split(" ")
                          .map((part) => part[0])
                          .join("")}
                      </span>
                      <b>{name}</b>
                    </span>
                  </td>
                  <td>{team}</td>
                  <td>{city}</td>
                  <td>
                    <span
                      className={
                        status === "Confirmed" ? "ew-green" : "ew-amber"
                      }
                    >
                      {status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {guests.length === 0 && (
            <p className="ew-empty" role="status">
              No sample guests match “{guestFilter}”. Try a name, team or city.
            </p>
          )}
        </div>
        <div className="ew-bottom-note">
          <span>
            <CheckCheck size={14} aria-hidden="true" /> 9 team leads invited ·
            Duplicates held for review
          </span>
          <button type="button" onClick={() => onNavigate("doors")}>
            View check-in <ArrowRight size={13} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ew-scene ew-doors">
      <div className="ew-door-count">
        <span className="ew-mini-label">
          BARCELONA KICKOFF · WELCOME RECEPTION
        </span>
        <h3>Guest arrivals</h3>
        <div>
          <strong>{scanned ? "119" : "118"}</strong>
          <span>
            / 140
            <br />
            guests checked in
          </span>
        </div>
        <p>19:11 · 2 staff at the entrance</p>
        <div className="ew-attendance" aria-hidden="true">
          {Array.from({ length: 140 }, (_, i) => (
            <i key={i} className={i < (scanned ? 119 : 118) ? "is-in" : ""} />
          ))}
        </div>
        <span className="ew-door-legend">
          <i /> Checked in <i /> Yet to arrive
        </span>
      </div>
      <div className="ew-checkin-pass">
        <span className="ew-mini-label">MAIN ENTRANCE</span>
        <span className="ew-scan-icon">
          <ScanLine size={50} strokeWidth={1} aria-hidden="true" />
        </span>
        <h4>{scanned ? "Welcome, Amelia." : "Guest check-in"}</h4>
        <p>
          {scanned
            ? "Amelia Shaw · Product · London"
            : "Check in Amelia to add her to the arrival count."}
        </p>
        <button
          type="button"
          className="ew-primary"
          onClick={() => setScanned(!scanned)}
        >
          {scanned ? (
            <>
              <Check size={15} aria-hidden="true" /> Reset sample check-in
            </>
          ) : (
            <>
              <ScanLine size={15} aria-hidden="true" /> Check in Amelia
            </>
          )}
        </button>
        <span role="status">
          {scanned
            ? "Guest checked in · Attendance updated"
            : "Interactive sample · No guest data affected"}
        </span>
      </div>
    </div>
  );
};

export default WorkspaceScene;
